import { useContext, useEffect, useMemo } from 'react';
import stockService from '@services/stock';

import { PriceSocketContext } from './PriceSocket';

/**
 * @typedef {object} PriceSocketObject
 *
 * @property  {number}  price
 * @property  {number}  change
 * @property  {number}  pctChange
 */

/**
 * @param {string} code
 */
function useRealtimeStockPrice(
  code,
  enabled = true,
  initialValue = { price: 0, return_amount: 0, return_pct: 0 }
) {
  const { data, isSuccess } = stockService.detail.price.usePrice(code, enabled);
  const { prices, listen, unlisten } = useContext(PriceSocketContext);

  useEffect(() => {
    if (typeof code !== 'string' && code === '' && code.toLowerCase() !== 'undefined') {
      return;
    }

    listen(code);

    return () => {
      unlisten(code);
    };
  }, [code, listen, unlisten]);

  const isDataReady = isSuccess || !!prices[code];

  /** @type {import('@services/stock/detail/price/typedefs').StockDetailPrice} */
  const realtimeData = useMemo(
    () => ({
      ...data,
      price: prices[code]?.price || data?.price || initialValue?.price || 0,
      return_amount:
        prices[code]?.change || data?.return_amount || initialValue?.return_amount || 0,
      return_pct: prices[code]?.pctChange || data?.return_pct || initialValue?.return_pct || 0,
    }),
    [data, prices[code]]
  );

  return { data: realtimeData, isDataReady };
}

export default useRealtimeStockPrice;
